import { getBreakoutById } from './catalog';
import type {
  Enrollment,
  MoveOutcome,
  MoveRecord,
  MoveTemplate,
  BreakoutDefinition,
  BreakoutStage,
} from '../types';

export const createEnrollment = (
  breakoutId: string,
  goalId: string,
  now = new Date(),
): Enrollment => ({
  id: `enr_${now.getTime()}_${Math.random().toString(36).slice(2, 8)}`,
  breakoutId,
  goalId,
  startedAt: now.toISOString(),
  currentDay: 1,
  status: 'active',
});

export const stageForDay = (
  breakout: BreakoutDefinition,
  day: number,
): BreakoutStage | undefined =>
  breakout.stages.find((s) => day >= s.dayStart && day <= s.dayEnd);

export const moveForDay = (
  breakout: BreakoutDefinition,
  day: number,
): MoveTemplate | undefined => breakout.moves.find((m) => m.day === day);

export const currentMove = (
  enrollment: Enrollment,
): { breakout: BreakoutDefinition; move: MoveTemplate; stage?: BreakoutStage } | null => {
  const breakout = getBreakoutById(enrollment.breakoutId);
  if (!breakout || enrollment.status !== 'active') return null;
  const move = moveForDay(breakout, enrollment.currentDay);
  if (!move) return null;
  return {
    breakout,
    move,
    stage: stageForDay(breakout, enrollment.currentDay),
  };
};

export const hasCompletedMoveForCurrentDay = (
  enrollment: Enrollment,
  records: MoveRecord[],
): boolean =>
  records.some(
    (r) =>
      r.enrollmentId === enrollment.id &&
      r.day === enrollment.currentDay &&
      r.outcome === 'complete',
  );

export interface CompleteMoveInput {
  enrollment: Enrollment;
  moveTemplateId: string;
  day: number;
  outcome: MoveOutcome;
  difficulty?: 1 | 2 | 3 | 4 | 5;
  note?: string;
  now?: Date;
}

export interface CompleteMoveResult {
  record: MoveRecord;
  nextEnrollment: Enrollment;
  advanced: boolean;
}

/**
 * Record a Move outcome and advance the day when completed.
 * Skip / hard still records progress history — never wipes enrollment day backward.
 */
export const completeMove = (input: CompleteMoveInput): CompleteMoveResult => {
  const now = input.now ?? new Date();
  const breakout = getBreakoutById(input.enrollment.breakoutId);
  if (!breakout) {
    throw new Error(`Unknown breakout: ${input.enrollment.breakoutId}`);
  }

  const record: MoveRecord = {
    id: `mv_${now.getTime()}_${Math.random().toString(36).slice(2, 8)}`,
    enrollmentId: input.enrollment.id,
    moveTemplateId: input.moveTemplateId,
    day: input.day,
    outcome: input.outcome,
    difficulty: input.difficulty,
    note: input.note,
    recordedAt: now.toISOString(),
  };

  let nextEnrollment: Enrollment = { ...input.enrollment };
  let advanced = false;

  if (input.outcome === 'complete' && input.day === input.enrollment.currentDay) {
    if (input.enrollment.currentDay >= breakout.durationDays) {
      nextEnrollment = { ...nextEnrollment, status: 'completed' };
    } else {
      nextEnrollment = {
        ...nextEnrollment,
        currentDay: input.enrollment.currentDay + 1,
      };
      advanced = true;
    }
  }

  // Skip and hard keep you on the same day — you can retry. No punishment reset.
  return { record, nextEnrollment, advanced };
};
